import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  MapPinIcon,
  StarIcon,
  BuildingStorefrontIcon,
  MagnifyingGlassIcon,
  ReceiptPercentIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Restaurants = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCuisine, setSelectedCuisine] = useState('all');

  useEffect(() => {
    requestLocationAndFetch();
  }, []);

  const requestLocationAndFetch = async () => {
    setLoading(true);

    // Try to get GPS location
    if ('geolocation' in navigator) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // Cache for 5 minutes
          });
        });

        const { latitude, longitude } = position.coords;

        // Save location to server
        try {
          await axios.post(`${API_URL}/neighborhood/location`, {
            latitude,
            longitude,
            enabled: true
          });
        } catch (e) {
          console.log('Could not save location to server');
        }

        // Fetch restaurants with location
        await fetchRestaurantsWithLocation(latitude, longitude);
      } catch (geoError) {
        console.log('Geolocation error:', geoError.message);
        // Fallback to all restaurants without distance
        await fetchRestaurantsFallback();
      }
    } else {
      await fetchRestaurantsFallback();
    }
  };

  const fetchRestaurantsWithLocation = async (latitude, longitude) => {
    try {
      const response = await axios.get(`${API_URL}/neighborhood/restaurants`, {
        params: { latitude, longitude }
      });
      setRestaurants(response.data.restaurants || []);
      setError('');
    } catch (error) {
      console.error('Error fetching neighborhood restaurants:', error);
      // Fallback to regular restaurants endpoint
      await fetchRestaurantsFallback();
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurantsFallback = async () => {
    try {
      const response = await axios.get(`${API_URL}/restaurants`);
      setRestaurants(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      setError('Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  // Get unique cuisines for filter
  const cuisines = ['all', ...new Set(restaurants.map(r => r.cuisine).filter(Boolean))];

  // Filter restaurants
  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = restaurant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          restaurant.cuisine?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCuisine = selectedCuisine === 'all' || restaurant.cuisine === selectedCuisine;
    return matchesSearch && matchesCuisine;
  });

  const handleRedeemClick = (restaurantId) => {
    navigate(`/redemption?restaurant=${restaurantId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading restaurants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-8">
      <SEO
        title="Restaurants Near You — Discounts with LOCU Points"
        description="Browse partner restaurants, cafes, and bars near you. Use LOCU points for up to 10% discounts on dining. Sorted by distance from your location."
        path="/restaurants"
        keywords="restaurant discounts, LOCU partner restaurants, dining deals near me, food discounts"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "LOCU Partner Restaurants",
          "description": "Partner restaurants where LOCU points can be redeemed for discounts",
          "url": "https://www.locu.in/restaurants"
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-100">
              <BuildingStorefrontIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Neighborhood Restaurants</h1>
              <p className="text-gray-600 mt-1">
                {user?.currentRewardBalance > 0
                  ? `You have ${user.currentRewardBalance} points to redeem!`
                  : 'Discover partner restaurants and redeem your points'}
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search restaurants or cuisine..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            {/* Cuisine Filter */}
            <select
              value={selectedCuisine}
              onChange={(e) => setSelectedCuisine(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {cuisines.map(cuisine => (
                <option key={cuisine} value={cuisine}>
                  {cuisine === 'all' ? 'All Cuisines' : cuisine}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Restaurant Grid */}
        {filteredRestaurants.length === 0 ? (
          <div className="text-center py-16">
            <BuildingStorefrontIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No restaurants found</h3>
            <p className="text-gray-500">
              {searchTerm || selectedCuisine !== 'all'
                ? 'Try adjusting your search or filter'
                : 'Partner restaurants will appear here soon'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRestaurants.map((restaurant) => (
              <RestaurantCard
                key={restaurant._id}
                restaurant={restaurant}
                onClick={() => navigate(`/restaurants/${restaurant._id}`)}
                onRedeem={() => handleRedeemClick(restaurant._id)}
                userPoints={user?.currentRewardBalance || 0}
              />
            ))}
          </div>
        )}

        {/* Points Banner */}
        {user?.currentRewardBalance > 0 && (
          <div className="mt-8 bg-gradient-to-r from-primary-500 to-primary-700 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-xl font-bold">Ready to Save?</h3>
                <p className="text-primary-100">
                  Use your {user.currentRewardBalance} points at any partner restaurant
                </p>
              </div>
              <button
                onClick={() => navigate('/redemption')}
                className="px-6 py-3 bg-white text-primary-600 rounded-xl font-semibold hover:bg-primary-50 transition"
              >
                Redeem Now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  // If it's already a full URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Otherwise, prepend the API base URL
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  // Remove /api suffix if present for static files
  const staticBaseUrl = baseUrl.replace('/api', '');
  return `${staticBaseUrl}${url}`;
};

// Format distance for display
const formatDistance = (distanceKm) => {
  if (distanceKm === null || distanceKm === undefined) return null;
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)}m`;
  }
  return `${distanceKm.toFixed(1)}km`;
};

// Restaurant Card Component
const RestaurantCard = ({ restaurant, onRedeem, userPoints, onClick }) => {
  const discountPercentage = restaurant.discountPercentage || 20;
  const distance = formatDistance(restaurant.distance);

  // Get cover image (first ambience or food photo)
  const coverPhoto = restaurant.photos?.find(p => p.category === 'ambience') ||
                     restaurant.photos?.find(p => p.category === 'food');
  const coverImage = getImageUrl(coverPhoto?.url);

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow">
      {/* Clickable area for details */}
      <div onClick={onClick} className="cursor-pointer">
        {/* Image */}
        <div className="h-48 bg-gradient-to-br from-primary-100 to-purple-100 relative">
          {coverImage ? (
            <img
              src={coverImage}
              alt={restaurant.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <BuildingStorefrontIcon className="h-16 w-16 text-primary-300" />
            </div>
          )}
          {/* Discount Badge */}
          <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
            {discountPercentage}% OFF
          </div>
          {/* Distance Badge */}
          {distance && (
            <div className="absolute top-3 left-3 bg-white/90 text-gray-700 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <MapPinIcon className="h-3 w-3" />
              {distance}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5 pb-3">
          <h3 className="text-xl font-bold text-gray-900 mb-1">{restaurant.name}</h3>

          {restaurant.cuisine && (
            <p className="text-primary-600 font-medium text-sm mb-2">{restaurant.cuisine}</p>
          )}

          {restaurant.address && (
            <div className="flex items-start gap-2 text-gray-600 text-sm mb-3">
              <MapPinIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span className="line-clamp-2">{restaurant.address}</span>
            </div>
          )}

          {/* Rating and Distance Row */}
          <div className="flex items-center gap-2">
            {restaurant.rating > 0 && (
              <div className="flex items-center gap-0.5 px-2 py-0.5 bg-green-500 text-white rounded text-sm font-semibold">
                {restaurant.rating.toFixed(1)}
                <StarSolidIcon className="h-3 w-3" />
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500 mt-2">
            Min. bill ₹300 • {discountPercentage}% off on food & beverages only
          </p>
        </div>
      </div>

      {/* Action Button - separate from clickable area */}
      <div className="px-5 pb-5">
        <button
          onClick={(e) => { e.stopPropagation(); onRedeem(); }}
          disabled={userPoints <= 0}
          className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition ${
            userPoints > 0
              ? 'bg-gradient-to-r from-primary-500 to-primary-700 text-white hover:shadow-md'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          <ReceiptPercentIcon className="h-5 w-5" />
          {userPoints > 0 ? 'Redeem Points Here' : 'Earn Points to Redeem'}
        </button>
      </div>
    </div>
  );
};

export default Restaurants;
